/**
 * pageDates — real publication dates carried over from WordPress.
 *
 * Every template used to stamp Article schema with the build date, which told
 * Google that a page written in 2021 was published today. Content age is a
 * signal these URLs have spent years earning; this table preserves it. Pages
 * with no WordPress counterpart fall back to the build date, which for them is
 * true.
 *
 * Source: wp post get <id> --field=post_date/post_modified, 2026-08-21.
 */
export interface PageDates {
  published: string;
  modified: string;
}

const PAGE_DATES: Record<string, PageDates> = {
  "/": { published: "2023-09-01", modified: "2026-06-17" },
  "/about-us/": { published: "2019-11-30", modified: "2026-05-19" },
  "/alan-vaitzman-esq/": { published: "2025-07-18", modified: "2026-06-03" },
  "/albany/": { published: "2023-08-21", modified: "2023-08-22" },
  "/appointment/": { published: "2020-06-23", modified: "2026-05-25" },
  "/asset-protection/": { published: "2023-08-25", modified: "2023-08-25" },
  "/attorney-executive-advisor-at-morgan-legal-group/": { published: "2024-09-11", modified: "2025-07-12" },
  "/bronx/": { published: "2023-08-21", modified: "2023-08-21" },
  "/brooklyn/": { published: "2025-12-11", modified: "2025-12-11" },
  "/buffalo/": { published: "2023-08-21", modified: "2026-04-26" },
  "/child-support/": { published: "2022-06-11", modified: "2026-03-22" },
  "/contact-us/": { published: "2019-11-30", modified: "2026-03-22" },
  "/custody-and-visitation/": { published: "2022-06-11", modified: "2026-03-22" },
  "/elder-law/": { published: "2023-08-25", modified: "2026-05-20" },
  "/estate-planning-lawyer-lgbtq-divorce/": { published: "2022-06-11", modified: "2026-03-22" },
  "/estate-planning/": { published: "2025-04-12", modified: "2026-05-20" },
  "/family-law/": { published: "2024-07-09", modified: "2026-05-19" },
  "/family-lawyer-divorce/": { published: "2022-06-12", modified: "2026-03-22" },
  "/glossary-of-legal-terms/": { published: "2019-12-18", modified: "2023-12-13" },
  "/guardianship/": { published: "2023-08-25", modified: "2023-08-25" },
  "/location/": { published: "2023-09-03", modified: "2025-07-26" },
  "/long-island/": { published: "2023-08-21", modified: "2023-08-21" },
  "/manhasset-ny/": { published: "2025-12-11", modified: "2025-12-11" },
  "/medicaid-planning/": { published: "2023-08-25", modified: "2026-05-20" },
  "/new-york/": { published: "2023-08-21", modified: "2023-08-21" },
  "/nj/": { published: "2023-08-21", modified: "2023-08-21" },
  "/nyc-an-overview-of-elder-law/": { published: "2021-04-19", modified: "2026-03-22" },
  "/nyc-are-there-different-types-of-probate-in-new-york/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-article-81-guardianship/": { published: "2021-04-19", modified: "2026-03-22" },
  "/nyc-can-making-a-gift-before-applying-to-medicaid-save-assets/": { published: "2021-04-20", modified: "2026-03-22" },
  "/nyc-creation-of-a-life-estate/": { published: "2021-04-04", modified: "2026-03-22" },
  "/nyc-elder-abuse/": { published: "2021-03-21", modified: "2026-03-22" },
  "/nyc-elder-law/": { published: "2021-03-21", modified: "2026-03-22" },
  "/nyc-home-transfers-and-retained-life-estates-in-new-york-state/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-how-do-you-ensure-that-funds-are-not-misused-in-a-trust/": { published: "2021-04-20", modified: "2026-03-22" },
  "/nyc-how-does-a-medicaid-asset-protection-trust-work/": { published: "2021-04-04", modified: "2026-03-22" },
  "/nyc-how-is-a-will-contested-in-new-york/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-legal-guardianship-for-disabled-or-special-needs-children/": { published: "2021-04-04", modified: "2026-03-22" },
  "/nyc-legal-guardianship-for-special-needs-child/": { published: "2021-04-14", modified: "2026-03-22" },
  "/nyc-medicaid-and-home-care/": { published: "2021-03-21", modified: "2026-03-22" },
  "/nyc-medicaid-application-process/": { published: "2021-04-04", modified: "2026-03-22" },
  "/nyc-probate-and-estate-administration-in-new-york/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-probate-proceeding-in-new-york/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-set-up-a-supplemental-needs-trust-for-government-benefits/": { published: "2021-04-20", modified: "2026-03-22" },
  "/nyc-special-needs-planning/": { published: "2021-03-20", modified: "2026-03-22" },
  "/nyc-special-needs-trust-in-new-york/": { published: "2021-04-20", modified: "2026-03-22" },
  "/nyc-the-common-challenges-faced-during-the-probate-process/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-transfer-of-your-home-and-medicaid-eligibility/": { published: "2021-04-04", modified: "2026-03-22" },
  "/nyc-what-are-the-common-mistakes-made-when-setting-up-a-special-needs-supplemental-needs-trust/": { published: "2021-04-29", modified: "2026-03-22" },
  "/nyc-what-are-the-main-ways-to-pay-for-long-term-care/": { published: "2021-04-19", modified: "2023-12-16" },
  "/nyc-what-are-the-top-misconceptions-about-the-probate-process/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-what-is-a-special-supplemental-needs-trust/": { published: "2021-04-20", modified: "2026-03-22" },
  "/nyc-what-is-the-look-back-period-in-new-york/": { published: "2021-04-04", modified: "2026-03-22" },
  "/nyc-what-is-your-experience-handling-probate-matters-in-new-york/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-what-issues-can-elder-law-planning-help-with/": { published: "2021-04-19", modified: "2026-03-22" },
  "/nyc-what-outcome-can-someone-expect-upon-completion-of-probate/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-what-sets-your-firm-apart-in-handling-probate-matters/": { published: "2021-05-16", modified: "2026-03-22" },
  "/nyc-when-it-comes-to-special-needs-planning-does-the-type-of-disability-matter/": { published: "2021-04-20", modified: "2026-03-22" },
  "/nyc-why-a-revocable-living-trust-can-affect-a-medicaid-spend-down/": { published: "2021-04-19", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/": { published: "2026-03-11", modified: "2026-04-11" },
  "/nyc-wills-and-trusts/formal-execution-and-attestation-requirements-for-a-valid-will/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/last-will-and-testament-in-new-york/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/medicaid-asset-protection-trust-in-new-york/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/pooled-income-trust-in-new-york/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/special-needs-trust-in-new-york/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/the-ancient-document-exception-for-a-ny-will/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc-wills-and-trusts/will-contests-probate-and-estate-litigation-in-new-york/": { published: "2021-06-08", modified: "2026-03-22" },
  "/nyc/": { published: "2025-12-11", modified: "2026-06-17" },
  "/obsessed-with-success/": { published: "2025-07-10", modified: "2025-08-18" },
  "/orange-county/": { published: "2023-08-21", modified: "2023-08-21" },
  "/our-fees/": { published: "2020-04-16", modified: "2023-07-27" },
  "/power-of-attorney/": { published: "2023-08-25", modified: "2023-08-25" },
  "/practice-areas/": { published: "2019-11-30", modified: "2026-08-03" },
  "/practices/": { published: "2023-08-25", modified: "2025-07-12" },
  "/privacy-policy-page/": { published: "2020-06-01", modified: "2020-06-01" },
  "/probate/": { published: "2025-06-21", modified: "2026-05-20" },
  "/publications/": { published: "2020-01-06", modified: "2025-07-12" },
  "/queens/": { published: "2025-12-11", modified: "2025-12-11" },
  "/resources/": { published: "2019-12-18", modified: "2019-12-18" },
  "/reviews/": { published: "2021-03-20", modified: "2026-03-03" },
  "/rochester/": { published: "2023-08-21", modified: "2023-08-21" },
  "/russel-morgan-esq/": { published: "2019-11-30", modified: "2025-11-14" },
  "/russel-morgan-estate-planning-lawyer-in-nyc/": { published: "2022-04-22", modified: "2026-04-08" },
  "/simge-karaboga-ozdemir/": { published: "2025-12-09", modified: "2025-12-09" },
  "/sofia-cristando-esq/": { published: "2025-07-18", modified: "2025-07-18" },
  "/southampton-ny/": { published: "2025-12-11", modified: "2026-06-17" },
  "/staten-island/": { published: "2023-08-21", modified: "2023-08-21" },
  "/suffolk-county/": { published: "2023-08-21", modified: "2023-08-21" },
  "/trusts/": { published: "2019-12-03", modified: "2022-02-08" },
  "/ulster-county/": { published: "2023-08-21", modified: "2023-08-21" },
  "/westchester/": { published: "2023-08-21", modified: "2023-08-21" },
  "/wills-and-trusts/": { published: "2023-08-27", modified: "2026-05-20" },
};

const BUILD_DATE = new Date().toISOString().split('T')[0];

export function pageDates(url: string): PageDates {
  return PAGE_DATES[url] ?? { published: BUILD_DATE, modified: BUILD_DATE };
}
